import type { ApiErrorShape, ConnectedStorageAccount, DashboardData, FileItem, FileListing, FolderAccess, ManagedUser, User, UserTutorialProgress } from "./types";

const csrfCookieName = "cloudesa_csrf";

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;

  constructor(message: string, status: number, details: ApiErrorShape = { error: message }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = details.code;
    this.requestId = details.requestId;
  }
}

function cookie(name: string): string | null {
  for (const part of document.cookie.split(";")) {
    const [key, ...value] = part.trim().split("=");
    if (key === name) return decodeURIComponent(value.join("="));
  }
  return null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase())) {
    const csrf = cookie(csrfCookieName);
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  const response = await fetch(`/api${path}`, { ...init, headers, credentials: "same-origin" });
  const contentType = response.headers.get("Content-Type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok) {
    const details = (payload ?? { error: "Request failed." }) as ApiErrorShape;
    throw new ApiError(details.error ?? "Request failed.", response.status, details);
  }
  return payload as T;
}

export const api = {
  login: (username: string, password: string) => request<{ user: User }>("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  me: () => request<{ user: User; expiresAt: number }>("/auth/me"),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST", body: "{}" }),
  changePassword: (currentPassword: string, newPassword: string) => request<{ ok: true }>("/auth/password", { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) }),
  users: () => request<{ users: ManagedUser[] }>("/users"),
  createUser: (input: { username: string; password: string; deviceLimit: number }) => request<{ user: ManagedUser }>("/users", { method: "POST", body: JSON.stringify(input) }),
  updateUser: (id: string, input: { deviceLimit?: number; password?: string }) => request<{ ok: true }>(`/users/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) }),
  revokeUserDevices: (id: string) => request<{ ok: true }>(`/users/${encodeURIComponent(id)}/devices`, { method: "DELETE", body: "{}" }),
  deleteUser: (id: string) => request<{ ok: true }>(`/users/${encodeURIComponent(id)}`, { method: "DELETE", body: "{}" }),
  userProgress: () => request<{ accountId: string; users: UserTutorialProgress[] }>("/users/progress"),
  dashboard: () => request<DashboardData>("/dashboard"),
  files: (params: { accountId?: string | null; parentId?: string | null; search?: string; category?: string; sort?: string; direction?: string } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return request<FileListing>(`/files${query.size ? `?${query.toString()}` : ""}`);
  },
  createFolder: (name: string, parentId: string | null, accountId: string) => request<FileItem>("/files/folder", { method: "POST", body: JSON.stringify({ name, parentId, accountId }) }),
  renameOrMove: (id: string, input: { name?: string; parentId?: string | null }, accountId: string) => request<FileItem>(`/files/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ ...input, accountId }) }),
  deleteFile: (id: string, accountId: string, permanent = false) => request<{ ok: true }>(`/files/${encodeURIComponent(id)}?permanent=${permanent}&accountId=${encodeURIComponent(accountId)}`, { method: "DELETE", body: "{}" }),
  folderAccess: (id: string, accountId: string) => request<FolderAccess>(`/files/${encodeURIComponent(id)}/access?accountId=${encodeURIComponent(accountId)}`),
  updateFolderAccess: (id: string, userIds: string[], accountId: string) => request<FolderAccess>(`/files/${encodeURIComponent(id)}/access`, { method: "PUT", body: JSON.stringify({ userIds, accountId }) }),
  setFileProgress: (id: string, completed: boolean, accountId: string) => request<{ id: string; completed: boolean }>(`/files/${encodeURIComponent(id)}/progress`, { method: "PUT", body: JSON.stringify({ completed, accountId }) }),
  uploadTicket: (file: File, parentId: string | null, accountId: string) => request<{ url: string; token: string; expiresAt: number }>("/files/upload-ticket", { method: "POST", body: JSON.stringify({ name: file.name, size: file.size, mimeType: file.type, parentId, accountId }) }),
  downloadTicket: (id: string, accountId: string, inline = false) => request<{ url: string; token: string; expiresAt: number }>(`/files/${encodeURIComponent(id)}/download-ticket`, { method: "POST", body: JSON.stringify({ inline, accountId }) }),
  storageAccounts: () => request<{ accounts: ConnectedStorageAccount[] }>("/accounts/storage"),
  activateStorageAccount: (id: string) => request<{ ok: true; id: string }>("/accounts/storage/activate", { method: "POST", body: JSON.stringify({ id }) }),
  removeStorageAccount: (id: string) => request<{ ok: true; id: string }>(`/accounts/storage/${encodeURIComponent(id)}`, { method: "DELETE", body: "{}" }),
  reconnect: () => request<{ ok: true }>("/accounts/storage/reconnect", { method: "POST", body: "{}" }),
  connectStorage: (input: { name: string; email: string; password: string; secondFactorCode?: string }) => request<DashboardData["account"]>("/accounts/storage/connect", { method: "POST", body: JSON.stringify(input) }),
};

export function uploadWithProgress(file: File, ticket: { url: string; token: string }, onProgress: (percent: number) => void, signal?: AbortSignal): Promise<FileItem> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", ticket.url);
    xhr.responseType = "json";
    xhr.setRequestHeader("Authorization", `Bearer ${ticket.token}`);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onerror = () => reject(new Error("The browser could not reach the secure upload service."));
    xhr.onabort = () => reject(new DOMException("Upload cancelled.", "AbortError"));
    xhr.onload = () => {
      const payload = xhr.response as FileItem | ApiErrorShape | null;
      if (xhr.status >= 200 && xhr.status < 300 && payload) {
        onProgress(100);
        resolve(payload as FileItem);
      } else {
        const message = payload && "error" in payload ? payload.error : "The upload failed.";
        reject(new ApiError(message, xhr.status));
      }
    };
    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

export async function directDownloadUrl(id: string, accountId: string, inline = false): Promise<string> {
  const ticket = await api.downloadTicket(id, accountId, inline);
  const url = new URL(ticket.url);
  url.searchParams.set("ticket", ticket.token);
  if (inline) url.searchParams.set("inline", "1");
  return url.toString();
}
